import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Collapse,
    ConfigProvider,
    Flex,
    Select,
    Tooltip,
    Typography,
} from '@app/ui';
import './SocialBenchmarking.scss';
import { SocialBenchmarkingContext } from './SocialBenchmarkingContext';
import { HighlightResults } from '@app/actions/audience/getSocialBenchmarkingAccountGroups';
import { InfoCircleOutlined } from '@ant-design/icons';
const { Text } = Typography;

type SocBenchHighlightsContainerProps = {
    children?: React.ReactNode;
    accountGroups: any[];
    highlights: HighlightResults;
};

const SocBenchHighlightsContainer = ({
    children,
    accountGroups,
    highlights,
}: SocBenchHighlightsContainerProps) => {
    const { t } = useTranslation('benchmarking');
    const { selectedAccountGroup, setSelectedAccountGroup } = useContext(
        SocialBenchmarkingContext,
    );

    const selectedAg =
        selectedAccountGroup && selectedAccountGroup.accountGroupId;

    const selectedAgName = selectedAccountGroup
        ? selectedAccountGroup.accountGroupName
        : t('socialBenchmarking.highlights.noRightsholderSelected');

    const onChangeSelectedAccountGroup = useCallback(
        accountGroupId => {
            const selected = accountGroupId
                ? {
                      accountGroupId,
                      ...highlights.data[accountGroupId],
                  }
                : null;
            setSelectedAccountGroup(selected);
        },
        [highlights],
    );

    const [isExpanded, setIsExpanded] = useState(true);

    const agOptions = useMemo(
        () =>
            accountGroups
                .map(ag => ({
                    name: ag.accountGroupName,
                    value: ag.accountGroupId,
                }))
                .sort((a, b) => a.name.localeCompare(b.name)),
        [accountGroups],
    );
    const titleContent = (
        <Flex>
            <Flex
                gap={12}
                justify="center"
                align="center"
                className="-m-r-2"
                style={{
                    height: 'fit-content',
                    fontSize: 20,
                }}
            >
                {t('socialBenchmarking.highlights.title', {
                    name: selectedAgName,
                })}
                <Tooltip title={t('socialBenchmarking.highlights.tooltip')}>
                    <InfoCircleOutlined
                        style={{
                            color: '#999',
                            fontSize: '14px',
                            marginLeft: 'auto',
                        }}
                    />
                </Tooltip>
            </Flex>
        </Flex>
    );

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginBottom: '16px',
                    marginTop: '16px',
                    alignItems: 'center',
                    gap: '8px',
                }}
            >
                <Text style={{ fontSize: '14px', color: '#666' }}>
                    {t('socialBenchmarking.highlights.label')}
                </Text>
                <Select
                    placeholder={t(
                        'socialBenchmarking.highlights.selectPlaceholder',
                    )}
                    value={selectedAg}
                    onChange={onChangeSelectedAccountGroup}
                    style={{ width: 200 }}
                    options={agOptions}
                    fieldNames={{ label: 'name', value: 'value' }}
                />
            </div>

            <ConfigProvider
                theme={{
                    components: {
                        Collapse: {
                            headerBg: '#FFF',
                            colorBorder: '#BFBFBF',
                        },
                    },
                }}
            >
                <Collapse
                    style={{
                        top: 0,
                        overflow: 'hidden',
                    }}
                    activeKey={isExpanded ? ['1'] : []}
                    onChange={() => setIsExpanded(!isExpanded)}
                    expandIconPosition="end"
                    items={[
                        {
                            key: '1',
                            label: titleContent,
                            children: (
                                <div className="filters-accordion__selects">
                                    {children}
                                </div>
                            ),
                        },
                    ]}
                />
            </ConfigProvider>
        </div>
    );
};

export default SocBenchHighlightsContainer;
